/*
 * Unzer Ratenzahlung – Storefront-Logik des App Blocks.
 */

(function () {
  'use strict';

  var GLOBAL_KEY = '__unzerInstallmentWidget';

  // Zweiter Aufruf des Assets (mehrere Blöcke, Re-Rendering): nur refreshen.
  if (window[GLOBAL_KEY] && typeof window[GLOBAL_KEY].refresh === 'function') {
    window[GLOBAL_KEY].refresh();
    return;
  }

  var WIDGET_SELECTOR = '[data-unzer-installment-widget]';
  var TEMPLATE_SELECTOR = '[data-unzer-tpl]';
  var VARIANTS_SELECTOR = 'script[data-unzer-installment-variants]';
  var TRIGGER_SELECTOR = '[data-unzer-installment-trigger]';
  var MODAL_SELECTOR = '[data-unzer-installment-modal]';
  var CLOSE_SELECTOR = '[data-unzer-installment-close]';
  var CLOSE_BUTTON_SELECTOR = 'button[data-unzer-installment-close]';
  var CURSOR_SELECTOR = '[data-unzer-installment-cursor]';
  var CALC_SELECTOR = '[data-unzer-installment-calc]';
  var TERM_SELECTOR = '[data-unzer-installment-term]';
  var CALC_RATE_SELECTOR = '[data-unzer-installment-calc-rate]';
  var CALC_NOTE_SELECTOR = '[data-unzer-installment-calc-note]';
  var PANEL_SELECTOR = '.unzer-modal__panel';
  var CART_FORM_SELECTOR = 'form[action*="/cart/add"]';
  var INSTANCE_KEY = '__unzerInstallmentInstance';

  /*
   * Sicherheitsnetz für Themes, die die Variante ausschließlich per JS setzen,
   * ohne `change`-Event, URL-Änderung oder Section Rendering. Der Tick liest nur
   * DOM-Werte und schreibt nur bei tatsächlicher Änderung.
   */
  var TICK_INTERVAL_MS = 1000;
  var DEBOUNCE_MS = 60;

  /* Muss zur Transition von `.unzer-modal__panel` im CSS passen. */
  var CLOSE_ANIMATION_MS = 340;

  /*
   * Best-effort-Beschleuniger: Shopify definiert derzeit kein themeübergreifendes
   * Standard-Event für Variantenwechsel. Die eigentliche Erkennung läuft über
   * DOM-Konventionen, MutationObserver und den Tick.
   */
  var THEME_VARIANT_EVENTS = [
    'variant:change',
    'variantChange',
    'variant:changed',
    'product:variant-change',
    'on:variant:change',
  ];

  // ------------------------------------------------------------------ Helpers

  function toInt(value, fallback) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
  }

  function toFloat(value, fallback) {
    var parsed = parseFloat(value);
    return isNaN(parsed) ? fallback : parsed;
  }

  /** Varianten-IDs werden immer als Ziffern-String verglichen. */
  function normalizeId(value) {
    if (value === null || value === undefined) return '';
    var stringValue = String(value).trim();
    return /^\d+$/.test(stringValue) ? stringValue : '';
  }

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        timer = null;
        fn();
      }, wait);
    };
  }

  // ------------------------------------------------------------ Konfiguration

  /** Liest die Block-Einstellungen; der Liquid-Block ist die einzige Quelle. */
  function readConfig(element) {
    var data = element.dataset;

    return {
      productId: normalizeId(data.productId),
      productUrl: data.productUrl || '',
      initialVariantId: normalizeId(data.variantId),
      initialPrice: toInt(data.price, null),
      currency: (data.currency || '').toUpperCase(),
      shopCurrency: (data.shopCurrency || '').toUpperCase(),
      locale: data.locale || document.documentElement.lang || undefined,
      installments: Math.max(1, toInt(data.installments, 12)),
      terms: parseTerms(data.terms, Math.max(1, toInt(data.installments, 12))),
      decimals: Math.min(2, Math.max(0, toInt(data.decimals, 2))),
      interestMode: data.interestMode === 'annuity' ? 'annuity' : 'none',
      effectiveInterest: Math.max(0, toFloat(data.effectiveInterest, 0)),
      /* Bereits in Liquid formatiert, damit Server und Client denselben Wert
         für `{interest}` ausgeben. */
      interestDisplay: data.interestDisplay || '0',
      // Grenzwerte kommen bereits in Minor Units (Cent) aus Liquid, 0 = aus.
      minAmount: Math.max(0, toInt(data.minAmount, 0)),
      maxAmount: Math.max(0, toInt(data.maxAmount, 0)),
    };
  }

  /**
   * Laufzeitliste aus dem Datenattribut. Aufsteigend sortiert und dedupliziert;
   * die längste Laufzeit ist die der Karte („ab …"). Unbrauchbare Eingaben
   * fallen auf die serverseitig gerenderte Laufzeit zurück, damit nie durch
   * Null geteilt wird.
   */
  function parseTerms(raw, fallback) {
    var seen = Object.create(null);
    var terms = [];
    var parts = String(raw || '').split(',');

    for (var i = 0; i < parts.length; i++) {
      var value = toInt(parts[i], 0);
      if (value > 0 && !seen[value]) {
        seen[value] = true;
        terms.push(value);
      }
    }

    if (!terms.length) return [fallback];

    terms.sort(function (a, b) {
      return a - b;
    });
    return terms;
  }

  // ---------------------------------------------------------- Textplatzhalter

  /**
   * Ersetzt `{rate}`, `{lastRate}`, `{total}`, `{price}`, `{months}` und
   * `{interest}`. Unbekannte Platzhalter bleiben unangetastet – so verrät ein
   * Tippfehler im Theme-Editor sich selbst, statt still zu verschwinden.
   */
  function renderTemplate(template, values) {
    return template.replace(/\{([a-zA-Z]+)\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  /** Alle Knoten, die einen variantenabhängigen Platzhalter enthalten. */
  function readTemplateSlots(element) {
    var nodes = element.querySelectorAll(TEMPLATE_SELECTOR);
    var slots = [];

    for (var i = 0; i < nodes.length; i++) {
      slots.push({
        node: nodes[i],
        template: nodes[i].getAttribute('data-unzer-tpl') || '',
      });
    }

    return slots;
  }

  // -------------------------------------------------------------- Preis-Index

  /** Varianten-ID -> Preis in Minor Units der aktuellen Präsentationswährung. */
  function createPriceIndex(element, config) {
    var prices = Object.create(null);
    var script = element.querySelector(VARIANTS_SELECTOR);

    if (script) {
      try {
        var parsed = JSON.parse(script.textContent || '{}');
        Object.keys(parsed).forEach(function (variantId) {
          var id = normalizeId(variantId);
          var price = toInt(parsed[variantId], null);
          if (id && price !== null) prices[id] = price;
        });
      } catch (error) {
        // Defektes JSON darf die Seite nicht stören.
      }
    }

    // Der serverseitig gerenderte Preis gilt für die Ausgangsvariante.
    if (config.initialVariantId && config.initialPrice !== null) {
      prices[config.initialVariantId] = config.initialPrice;
    }

    return {
      has: function (variantId) {
        return variantId !== '' && prices[variantId] !== undefined;
      },
      get: function (variantId) {
        var price = prices[variantId];
        return price === undefined ? null : price;
      },
      merge: function (additional) {
        Object.keys(additional).forEach(function (variantId) {
          if (prices[variantId] === undefined) prices[variantId] = additional[variantId];
        });
      },
    };
  }

  var variantRequests = Object.create(null);

  /*
   * Nachladen der Preise für Produkte mit mehr als 250 Varianten (Liquid-Limit).
   * Genutzt wird die öffentliche Storefront-Route `<product-url>.js`. Sie liefert
   * Preise in Shop-Basiswährung, deshalb nur bei identischer Präsentationswährung.
   * Ein Request pro Produkt und Seitenaufruf.
   */
  function fetchAdditionalVariants(config) {
    if (!config.productUrl || typeof window.fetch !== 'function') return null;
    if (!config.shopCurrency || config.shopCurrency !== config.currency) return null;

    var url = config.productUrl.split('?')[0] + '.js';
    if (variantRequests[url]) return variantRequests[url];

    variantRequests[url] = fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (payload) {
        var prices = Object.create(null);
        if (!payload || !payload.variants) return prices;

        payload.variants.forEach(function (variant) {
          var id = normalizeId(variant.id);
          var price = toInt(variant.price, null);
          if (id && price !== null) prices[id] = price;
        });
        return prices;
      })
      .catch(function () {
        return Object.create(null);
      });

    return variantRequests[url];
  }

  // ------------------------------------------------------ Variantenerkennung

  /** Suchbereich: bevorzugt die eigene Section, damit fremde Formulare außen bleiben. */
  function findScope(element) {
    return element.closest('[id^="shopify-section"]') || document;
  }

  /** Produkt-ID eines Formulars, sofern das Theme sie bereitstellt. */
  function readFormProductId(form) {
    var input = form.querySelector('[name="product-id"]');
    if (input) return normalizeId(input.value);
    if (form.dataset && form.dataset.productId) return normalizeId(form.dataset.productId);
    return '';
  }

  /*
   * Ermittelt die aktuelle Varianten-ID:
   *   1. Warenkorb-Formular der eigenen Section (`[name="id"]`), validiert gegen
   *      die bekannten Varianten-IDs des Produkts,
   *   2. `?variant=` in der URL,
   *   3. serverseitig gerenderte Ausgangsvariante.
   */
  function resolveVariantId(element, config, priceIndex) {
    var forms = findScope(element).querySelectorAll(CART_FORM_SELECTOR);
    var fallbackCandidate = '';

    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];
      var input = form.querySelector('[name="id"]');
      if (!input) continue;

      var variantId = normalizeId(input.value);
      if (!variantId) continue;

      var formProductId = readFormProductId(form);
      if (formProductId && config.productId && formProductId !== config.productId) {
        continue; // Formular eines anderen Produkts
      }

      // Bekannte Variante des eigenen Produkts: sicherster Treffer.
      if (priceIndex.has(variantId)) return variantId;

      // Unbekannte ID (z. B. >250 Varianten) nur mit passender Produkt-ID merken.
      if (!fallbackCandidate && formProductId && formProductId === config.productId) {
        fallbackCandidate = variantId;
      }
    }

    if (fallbackCandidate) return fallbackCandidate;

    try {
      var fromUrl = normalizeId(new URLSearchParams(window.location.search).get('variant'));
      if (fromUrl && (priceIndex.has(fromUrl) || !config.initialVariantId)) return fromUrl;
    } catch (error) {
      // URLSearchParams nicht verfügbar – unkritisch.
    }

    return config.initialVariantId;
  }

  // ------------------------------------------------------------------ Rechnung

  /*
   * Ratenplan in Minor Units.
   *
   * Die Rate wird auf Cent **abgerundet**, der Restbetrag landet auf der
   * letzten Rate. Das ist der Unterschied zu kaufmännischem Runden: Bei
   * 1.199,00 € auf 12 Monate ergibt Runden 12 × 99,92 € = 1.199,04 €, also
   * vier Cent zu viel. Abrunden ergibt 11 × 99,91 € + 99,99 € und trifft die
   * Summe exakt – und keine Rate ist höher als die beworbene.
   *
   *   none    – zinsfrei: Preis / Laufzeit
   *   annuity – Annuität; der effektive Jahreszins wird konform in einen
   *             Monatszins umgerechnet: i = (1 + p)^(1/12) − 1
   *
   * Beides ist eine unverbindliche Beispielrechnung (siehe docs/DECISIONS.md).
   */
  function buildPlan(priceInMinorUnits, term, config) {
    if (!term || term < 1) return null;
    if (priceInMinorUnits === null || priceInMinorUnits <= 0) return null;

    function split(total) {
      var base = Math.floor(total / term);
      var last = total - base * (term - 1);
      return { base: base, last: last, even: base === last, total: total };
    }

    if (config.interestMode !== 'annuity' || config.effectiveInterest <= 0) {
      return split(priceInMinorUnits);
    }

    var monthlyRate = Math.pow(1 + config.effectiveInterest / 100, 1 / 12) - 1;
    if (!isFinite(monthlyRate) || monthlyRate <= 0) return split(priceInMinorUnits);

    var payment =
      (priceInMinorUnits * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -term));
    if (!isFinite(payment) || payment <= 0) return null;

    // Im Annuitätsmodus ist die Summe der Raten höher als der Warenwert.
    return split(Math.round(payment * term));
  }

  /** Längste Laufzeit – die der Karte („ab …"). */
  function leadTermOf(config) {
    var terms = config.terms;
    return terms && terms.length ? terms[terms.length - 1] : config.installments;
  }

  /** Währungsformatierung über Intl.NumberFormat, mit robustem Fallback. */
  function createMoneyFormatter(config) {
    var options = {
      style: 'currency',
      currency: config.currency,
      minimumFractionDigits: config.decimals,
      maximumFractionDigits: config.decimals,
    };

    var formatter = null;
    try {
      formatter = new Intl.NumberFormat(config.locale, options);
    } catch (error) {
      try {
        formatter = new Intl.NumberFormat(undefined, options);
      } catch (innerError) {
        formatter = null;
      }
    }

    return function (minorUnits) {
      var majorUnits = minorUnits / 100;
      if (formatter) return formatter.format(majorUnits);
      return majorUnits.toFixed(config.decimals) + ' ' + config.currency;
    };
  }

  // -------------------------------------------------------------- Scroll-Lock

  /*
   * `showModal()` deckt die Seite ab, verhindert aber kein Scrollen dahinter.
   * Der Lock zählt mit, damit mehrere Blöcke auf einer Seite sich nicht
   * gegenseitig den ursprünglichen Wert überschreiben.
   */
  var scrollLocks = 0;
  var savedOverflow = '';

  function lockScroll() {
    scrollLocks += 1;
    if (scrollLocks > 1) return;
    savedOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }

  function unlockScroll() {
    if (scrollLocks === 0) return;
    scrollLocks -= 1;
    if (scrollLocks > 0) return;
    document.documentElement.style.overflow = savedOverflow;
  }

  // ------------------------------------------------------------------ Dialog

  /*
   * Steuert den Info-Dialog. Fokusfalle, ESC und Top-Layer liefert das native
   * <dialog> über `showModal()`; hier kommen nur die Ein-/Ausblend-Animation,
   * der Scroll-Lock und der Fallback für Engines ohne `showModal()` dazu.
   */
  function createModalController(element) {
    var trigger = element.querySelector(TRIGGER_SELECTOR);
    if (!trigger) return null;

    /*
     * Normalerweise steht der Dialog im Block. Einige Themes hängen
     * `<dialog>`-Elemente aber beim Laden in den `<body>` um – lief deren
     * Skript vor unserem, ist er hier nicht mehr zu finden. `aria-controls`
     * verweist ohnehin schon auf die ID, also zweiter Versuch über das
     * Dokument.
     */
    var modal = element.querySelector(MODAL_SELECTOR);
    if (!modal) {
      var modalId = trigger.getAttribute('aria-controls');
      if (modalId) modal = document.getElementById(modalId);
    }
    if (!modal) return null;

    var panel = modal.querySelector(PANEL_SELECTOR);
    var cursor = modal.querySelector(CURSOR_SELECTOR);
    var closeButton = modal.querySelector(CLOSE_BUTTON_SELECTOR);
    var state = 'closed'; // closed | open | closing
    var closeTimer = null;

    /*
     * `is-native` merkt sich, ob `showModal()` gegriffen hat. Nur dann gibt es
     * ein `::backdrop`, über das das CSS abdunkelt; sonst übernimmt der
     * Scrim-Div. Ohne diese Unterscheidung würden sich beide Ebenen zu
     * doppelter Dunkelheit addieren.
     */
    function showElement() {
      if (typeof modal.showModal === 'function') {
        try {
          modal.showModal();
          modal.classList.add('is-native');
          return;
        } catch (error) {
          // Bereits offen oder nicht im Dokument – Fallback unten.
        }
      }
      modal.classList.remove('is-native');
      modal.setAttribute('open', 'open');
    }

    function hideElement() {
      if (modal.open && typeof modal.close === 'function') {
        try {
          modal.close();
        } catch (error) {
          // Ignorieren: Das Attribut unten räumt in jedem Fall auf.
        }
      }
      modal.removeAttribute('open');
    }

    function open() {
      if (state === 'open') return;

      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }

      if (state === 'closed') {
        showElement();
        lockScroll();
      }

      state = 'open';
      trigger.setAttribute('aria-expanded', 'true');

      // Erzwingt den Ausgangszustand der Transition, bevor die Klasse greift.
      if (panel) void panel.offsetWidth;
      modal.classList.add('is-open');

      if (closeButton) {
        try {
          closeButton.focus({ preventScroll: true });
        } catch (error) {
          closeButton.focus();
        }
      }
    }

    function finishClose() {
      closeTimer = null;
      if (state !== 'closing') return;

      state = 'closed';
      hideElement();
      unlockScroll();
    }

    function close(returnFocus) {
      if (state !== 'open') return;

      state = 'closing';
      modal.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      deactivateCursor();

      if (returnFocus && trigger.isConnected) {
        try {
          trigger.focus({ preventScroll: true });
        } catch (error) {
          trigger.focus();
        }
      }

      closeTimer = setTimeout(finishClose, CLOSE_ANIMATION_MS);
    }

    trigger.addEventListener('click', function (event) {
      event.preventDefault();
      open();
    });

    modal.addEventListener('click', function (event) {
      // Scrim und Schließen-Button teilen sich dasselbe Datenattribut.
      if (event.target.closest && event.target.closest(CLOSE_SELECTOR)) {
        event.preventDefault();
        close(true);
        return;
      }

      /*
       * Klick neben das Panel. Normalerweise fängt ihn der Scrim ab; trifft er
       * das <dialog> selbst, deckt der Scrim die Fläche nicht (z. B. weil das
       * Theme das Element anders dimensioniert). Beides schließt.
       */
      if (event.target === modal) {
        close(true);
        return;
      }

      // Letzte Instanz: alles, was nachweislich außerhalb des Panels liegt.
      if (panel && event.target.closest && !event.target.closest(PANEL_SELECTOR)) {
        close(true);
      }
    });

    // ESC: Der Browser würde sofort schließen – hier zuerst die Animation.
    modal.addEventListener('cancel', function (event) {
      event.preventDefault();
      close(true);
    });

    // Fallback ohne `showModal()`: ESC muss selbst behandelt werden.
    modal.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        close(true);
      }
    });

    /* Von außen geschlossen (z. B. `dialog.close()` durch das Theme). */
    modal.addEventListener('close', function () {
      if (state === 'closed') return;

      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }

      state = 'closed';
      modal.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      unlockScroll();
    });

    /*
     * Ersatz-Mauszeiger über der Fläche neben dem Panel: ein mitlaufender
     * Kreis mit X, wie im Referenz-Drawer. Er ersetzt den Systemzeiger nur,
     * wenn er auch wirklich bewegt wird – deshalb setzt erst diese Funktion
     * die Klasse `has-cursor`, an der im CSS `cursor: none` hängt.
     * Auf Touch-Geräten bleibt alles aus: dort gibt es keinen Zeiger, den man
     * ersetzen könnte.
     */
    function isOutsidePanel(target) {
      if (!panel || !target || !target.closest) return true;
      return !target.closest(PANEL_SELECTOR);
    }

    function deactivateCursor() {
      if (!cursor) return;
      cursor.classList.remove('is-active');
      cursor.classList.remove('is-pressed');
    }

    function setupCursor() {
      if (!cursor) return;

      var finePointer = false;
      try {
        finePointer = window.matchMedia('(pointer: fine)').matches;
      } catch (error) {
        finePointer = false;
      }
      if (!finePointer) return;

      modal.classList.add('has-cursor');

      modal.addEventListener('pointermove', function (event) {
        cursor.style.setProperty('--unzer-cursor-x', event.clientX + 'px');
        cursor.style.setProperty('--unzer-cursor-y', event.clientY + 'px');
        cursor.classList.toggle('is-active', isOutsidePanel(event.target));
      });

      modal.addEventListener('pointerdown', function (event) {
        if (isOutsidePanel(event.target)) cursor.classList.add('is-pressed');
      });

      modal.addEventListener('pointerup', function () {
        cursor.classList.remove('is-pressed');
      });

      modal.addEventListener('pointerleave', deactivateCursor);
    }

    setupCursor();

    // Ab hier ist die Karte bedienbar (siehe `disabled` im Liquid-Block).
    trigger.removeAttribute('disabled');

    return {
      element: modal,
      close: function () {
        close(false);
      },
      /* Aufräumen, wenn der Block aus dem DOM verschwindet, während der
         Dialog offen ist – sonst bliebe der Scroll-Lock stehen. */
      destroy: function () {
        if (closeTimer) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }
        if (state !== 'closed') {
          state = 'closed';
          hideElement();
          unlockScroll();
        }
      },
    };
  }

  // ------------------------------------------------------------------ Rechner

  /*
   * Laufzeit wählen, Rate sofort sehen. Rein clientseitig: derselbe Ratenplan
   * wie für die Karte, nur mit der gewählten statt der längsten Laufzeit.
   *
   * Die Buttons kommen `disabled` aus dem Liquid und werden hier aktiviert –
   * ohne JavaScript wäre der Rechner eine Attrappe.
   */
  function createCalculator(scope, config, formatMoney) {
    if (!scope) return null;

    var root = scope.querySelector(CALC_SELECTOR);
    if (!root) return null;

    var buttons = root.querySelectorAll(TERM_SELECTOR);
    if (!buttons.length) return null;

    var rateNode = root.querySelector(CALC_RATE_SELECTOR);
    var noteNode = root.querySelector(CALC_NOTE_SELECTOR);
    var noteTemplate = noteNode
      ? noteNode.getAttribute('data-unzer-installment-calc-note') || ''
      : '';

    var selected = leadTermOf(config);
    var price = null;

    function setText(node, text) {
      if (node && node.textContent !== text) node.textContent = text;
    }

    function paint() {
      for (var i = 0; i < buttons.length; i++) {
        var term = toInt(buttons[i].getAttribute('data-unzer-installment-term'), 0);
        buttons[i].setAttribute('aria-pressed', String(term === selected));
      }

      if (price === null) return;

      var plan = buildPlan(price, selected, config);
      if (!plan) return;

      setText(rateNode, formatMoney(plan.base));

      // Der Hinweis erscheint nur, wenn die letzte Rate tatsächlich abweicht.
      if (noteNode) {
        var note = '';
        if (noteTemplate && !plan.even) {
          note = renderTemplate(noteTemplate, {
            lastRate: formatMoney(plan.last),
            rate: formatMoney(plan.base),
            total: formatMoney(plan.total),
            months: String(selected),
            interest: config.interestDisplay,
          });
        }
        setText(noteNode, note);
      }
    }

    for (var i = 0; i < buttons.length; i++) {
      buttons[i].removeAttribute('disabled');
      buttons[i].addEventListener('click', function (event) {
        var term = toInt(
          event.currentTarget.getAttribute('data-unzer-installment-term'),
          0,
        );
        if (term > 0) {
          selected = term;
          paint();
        }
      });
    }

    paint();

    return {
      setPrice: function (value) {
        price = value;
        paint();
      },
    };
  }

  // -------------------------------------------------------------------- Widget

  function createWidget(element) {
    var config = readConfig(element);
    var priceIndex = createPriceIndex(element, config);
    var formatMoney = createMoneyFormatter(config);
    var modal = createModalController(element);
    var variantsRequested = false;

    /* Der Rechner steht im Dialog – der kann vom Theme ausgehängt worden sein. */
    var calculator =
      createCalculator(element, config, formatMoney) ||
      (modal && modal.element
        ? createCalculator(modal.element, config, formatMoney)
        : null);

    var slots = readTemplateSlots(element);
    // Liegt der Dialog nicht mehr im Block, gehören seine Platzhalter trotzdem dazu.
    if (modal && modal.element && !element.contains(modal.element)) {
      slots = slots.concat(readTemplateSlots(modal.element));
    }

    function hide() {
      if (modal) modal.close();
      if (!element.hasAttribute('hidden')) element.setAttribute('hidden', 'hidden');
    }

    function show(plan, priceInMinorUnits) {
      var leadTerm = leadTermOf(config);
      var values = {
        rate: formatMoney(plan.base),
        lastRate: formatMoney(plan.last),
        total: formatMoney(plan.total),
        price: formatMoney(priceInMinorUnits),
        months: String(leadTerm),
        interest: config.interestDisplay,
      };

      if (calculator) calculator.setPrice(priceInMinorUnits);

      for (var i = 0; i < slots.length; i++) {
        var text = renderTemplate(slots[i].template, values);
        // Nur bei echter Änderung schreiben: Der MutationObserver dieses
        // Skripts beobachtet denselben Baum.
        if (slots[i].node.textContent !== text) slots[i].node.textContent = text;
      }

      if (element.hasAttribute('hidden')) element.removeAttribute('hidden');
    }

    function requestMissingVariants() {
      if (variantsRequested) return;
      variantsRequested = true;

      var request = fetchAdditionalVariants(config);
      if (!request) return;

      request.then(function (prices) {
        if (!element.isConnected) return;
        priceIndex.merge(prices);
        update();
      });
    }

    function update() {
      if (!element.isConnected) return;

      var price = priceIndex.get(resolveVariantId(element, config, priceIndex));

      if (price === null) {
        // Variante (noch) unbekannt: nichts anzeigen statt falsch anzeigen.
        requestMissingVariants();
        hide();
        return;
      }

      if (config.minAmount > 0 && price < config.minAmount) return hide();
      if (config.maxAmount > 0 && price > config.maxAmount) return hide();

      var plan = buildPlan(price, leadTermOf(config), config);
      if (plan === null) return hide();

      show(plan, price);
    }

    return {
      element: element,
      update: function () {
        try {
          update();
        } catch (error) {
          hide(); // Im Fehlerfall lieber nichts als etwas Falsches anzeigen.
        }
      },
      destroy: function () {
        if (modal) modal.destroy();
      },
    };
  }

  // ---------------------------------------------------------------- Controller

  var widgets = [];
  var ticker = null;
  var mutationObserver = null;

  /** Neue Blöcke initialisieren, verwaiste Instanzen freigeben. */
  function scan() {
    widgets = widgets.filter(function (widget) {
      if (widget.element.isConnected) return true;
      widget.element[INSTANCE_KEY] = null;
      widget.destroy();
      return false;
    });

    var elements = document.querySelectorAll(WIDGET_SELECTOR);
    for (var i = 0; i < elements.length; i++) {
      var element = elements[i];
      if (element[INSTANCE_KEY]) continue;

      try {
        var widget = createWidget(element);
        element[INSTANCE_KEY] = widget;
        widgets.push(widget);
        widget.update();
      } catch (error) {
        element[INSTANCE_KEY] = null; // Ein defekter Block blockiert die anderen nicht.
      }
    }
  }

  function refresh() {
    scan();
    for (var i = 0; i < widgets.length; i++) widgets[i].update();
  }

  var refreshSoon = debounce(refresh, DEBOUNCE_MS);

  function startTicker() {
    if (ticker !== null) return;
    ticker = setInterval(function () {
      if (document.hidden) return; // im Hintergrund-Tab nichts tun
      refresh();
    }, TICK_INTERVAL_MS);
  }

  function observeDom() {
    if (mutationObserver || typeof MutationObserver !== 'function') return;

    // Erkennt Section Rendering: der Block wird dabei im DOM ersetzt.
    mutationObserver = new MutationObserver(refreshSoon);
    mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  function bindEvents() {
    // Variantenauswahl über Radios, Selects oder Inputs des Produktformulars.
    document.addEventListener('change', refreshSoon, true);
    document.addEventListener('input', refreshSoon, true);

    // Navigation innerhalb der Produktseite (`?variant=`).
    window.addEventListener('popstate', refreshSoon);
    window.addEventListener('pageshow', refreshSoon);

    THEME_VARIANT_EVENTS.forEach(function (eventName) {
      document.addEventListener(eventName, refreshSoon);
    });

    // Theme-Editor: Sections und Blöcke werden dynamisch geladen und entfernt.
    document.addEventListener('shopify:section:load', refreshSoon);
    document.addEventListener('shopify:section:unload', refreshSoon);
    document.addEventListener('shopify:section:reorder', refreshSoon);
    document.addEventListener('shopify:block:select', refreshSoon);
    document.addEventListener('shopify:block:deselect', refreshSoon);
  }

  function start() {
    refresh();
    bindEvents();
    observeDom();
    startTicker();
  }

  window[GLOBAL_KEY] = { refresh: refresh, version: '2.0.0' };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
